import React, { useState } from "react";
import { fetchSystemLogs } from "../../api/systemLogService";
import { predictAnomalies } from "../../api/anomalyDetectionService";

const AnomalyDetectionPanel = () => {
    const [inputFeatures, setInputFeatures] = useState([]);
    const [result, setResult] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const demoData = [
        { feature1: 10, feature2: 5, feature3: 2, timestamp: new Date().toISOString() },
        { feature1: 12, feature2: 6, feature3: 3, timestamp: new Date().toISOString() },
        { feature1: 100, feature2: 1, feature3: 0, timestamp: new Date().toISOString() },
    ];

    const buildFeaturesFromLogs = (logs) => {
        return logs.slice(0, 20).map((log) => {
            const level = (log.level || "").toLowerCase();
            const messageLength = log.message?.length || 0;

            return {
                feature1: level === "error" ? 100 : level === "warning" ? 50 : 10,
                feature2: messageLength,
                feature3: log.id || 0,
                timestamp: log.timestamp || new Date().toISOString(),
            };
        });
    };

    const normalizeResult = (data) => {
        const raw = data?.data ?? data?.predictions ?? data;
        if (Array.isArray(raw)) return raw;
        if (raw && typeof raw === "object") return [raw];
        return [];
    };

    const getFriendlyError = (err) => {
        if (err?.response) {
            return (
                err.response.data?.message ||
                err.response.data?.error ||
                `Server returned status ${err.response.status}.`
            );
        }

        if (err?.request) {
            return "Backend is not available or CORS blocked the request. Check API server, domain, and token.";
        }

        return err?.message || "Failed to run anomaly detection.";
    };

    const runDetection = async () => {
        setLoading(true);
        setError("");
        setResult([]);
        setInputFeatures([]);

        try {
            let features = [];

            try {
                const logs = await fetchSystemLogs();
                features = Array.isArray(logs) && logs.length > 0
                    ? buildFeaturesFromLogs(logs)
                    : demoData;
            } catch {
                features = demoData;
            }

            setInputFeatures(features);

            const data = await predictAnomalies(features);
            setResult(normalizeResult(data));
        } catch (err) {
            console.error("Anomaly detection error:", err);
            setError(getFriendlyError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="p-4 bg-white rounded shadow">
            <h2 className="text-xl font-bold mb-2">Anomaly Detection</h2>

            <p className="text-sm text-gray-600 mb-4">
                This module tries to build anomaly detection features from backend
                SystemLog records. If logs are unavailable, it uses demo fallback data.
            </p>

            <button
                onClick={runDetection}
                disabled={loading}
                className="px-4 py-2 rounded bg-blue-600 text-white disabled:opacity-60"
            >
                {loading ? "Running..." : "Run Detection"}
            </button>

            {error && (
                <div className="mt-4 p-3 rounded border border-red-300 bg-red-50 text-red-700">
                    {error}
                </div>
            )}

            {inputFeatures.length > 0 && (
                <div className="bg-gray-50 border rounded p-3 mt-4">
                    <h3 className="font-semibold mb-2">Input features</h3>

                    <table className="w-full text-sm border">
                        <thead>
                            <tr>
                                <th className="border p-2">Feature 1</th>
                                <th className="border p-2">Feature 2</th>
                                <th className="border p-2">Feature 3</th>
                                <th className="border p-2">Timestamp</th>
                            </tr>
                        </thead>

                        <tbody>
                            {inputFeatures.map((item, index) => (
                                <tr key={index}>
                                    <td className="border p-2">{item.feature1}</td>
                                    <td className="border p-2">{item.feature2}</td>
                                    <td className="border p-2">{item.feature3}</td>
                                    <td className="border p-2">
                                        {new Date(item.timestamp).toLocaleString()}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {result.length > 0 && (
                <div className="mt-4">
                    <h3 className="font-semibold mb-2">Detection result</h3>

                    <table className="w-full text-sm border">
                        <thead>
                            <tr>
                                <th className="border p-2">Index</th>
                                <th className="border p-2">Is Anomaly</th>
                                <th className="border p-2">Score</th>
                                <th className="border p-2">Message</th>
                            </tr>
                        </thead>

                        <tbody>
                            {result.map((item, index) => (
                                <tr key={item.index ?? index}>
                                    <td className="border p-2">{item.index ?? index}</td>
                                    <td className="border p-2">{item.isAnomaly ? "Yes" : "No"}</td>
                                    <td className="border p-2">
                                        {item.score !== undefined ? Number(item.score).toFixed(6) : "�"}
                                    </td>
                                    <td className="border p-2">{item.message || "�"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default AnomalyDetectionPanel;